import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useAuth } from './lib/AuthContext';
import { fmtDay } from './lib/format';
import { orgDisplayName } from './lib/healthStrip';
import { isEmbedded } from './lib/embedTokenHandoff';
import { readParentTheme, applyTheme, acceptsParentTheme } from './lib/embeddedTheme';
import Sidebar from './components/Sidebar';
import { AndreWorkspace, AskAndreButton } from './components/andre/AndreWorkspace';
import PageLayout from './components/PageLayout';
import LoginPage from './pages/LoginPage';
import { Loader2 } from 'lucide-react';

/* ──────────────────────────────────────────────────────────
   Page imports — lazy-loaded so the app boots even when
   individual page modules haven't been created yet.
   ────────────────────────────────────────────────────────── */

const TachoDashboard = lazy(() =>
  import('./pages/TachoDashboard').catch(() => ({
    default: () => <PlaceholderPage label="Tachograph Analysis" />,
  }))
);

const TachoManagementReport = lazy(() =>
  import('./pages/TachoManagementReport').catch(() => ({
    default: () => <PlaceholderPage label="Risk report" />,
  }))
);

const TachoReportSuite = lazy(() =>
  import('./pages/TachoReportSuite').catch(() => ({
    default: () => <PlaceholderPage label="Report Suite" />,
  }))
);

const UserManagement = lazy(() =>
  import('./pages/UserManagement').catch(() => ({
    default: () => <PlaceholderPage label="User Management" />,
  }))
);

const SuperAdminDashboard = lazy(() =>
  import('./pages/SuperAdminDashboard').catch(() => ({
    default: () => <PlaceholderPage label="Super Admin" />,
  }))
);

const OrgSettings = lazy(() =>
  import('./pages/OrgSettings').catch(() => ({
    default: () => <PlaceholderPage label="Org Settings" />,
  }))
);

const Quarantine = lazy(() =>
  import('./pages/Quarantine').catch(() => ({
    default: () => <PlaceholderPage label="Files we couldn't place" />,
  }))
);

const DeepDivePage = lazy(() =>
  import('./pages/DeepDivePage').catch(() => ({
    default: () => <PlaceholderPage label="Deep Dive" />,
  }))
);

const FileViewerPage = lazy(() =>
  import('./pages/FileViewerPage').catch(() => ({
    default: () => <PlaceholderPage label="File Viewer" />,
  }))
);

const AnalogueCharts = lazy(() =>
  import('./pages/AnalogueCharts').catch(() => ({
    default: () => <PlaceholderPage label="Analogue charts" />,
  }))
);

/* ── Placeholder for a page module that failed to load ── */
function PlaceholderPage({ label }) {
  return (
    <div className="state-view">
      <h3 className="state-view-title">{label} isn&apos;t available</h3>
      <p className="state-view-body">This page could not be loaded. Reload to try again.</p>
    </div>
  );
}

/* ── Export: the old placeholder. Exports are made in Report suite, so an
   old link to this page says so and offers the way there. ── */
function ExportDataPage({ onOpenReports }) {
  return (
    <div className="state-view">
      <h3 className="state-view-title">Exports are made in Report suite</h3>
      <p className="state-view-body">Choose a report, set the dates and who it covers, then download it as a PDF or CSV.</p>
      <button type="button" className="btn btn--secondary" onClick={onOpenReports}>Open Report suite</button>
    </div>
  );
}

/* ── Loading spinner for Suspense fallback ── */
function PageSpinner() {
  return (
    <div className="flex items-center justify-center" style={{ minHeight: '40vh' }}>
      <Loader2 size={32} className="animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   View metadata
   ────────────────────────────────────────────────────────── */

const VIEW_META = {
  tachograph: {
    // Front-door pass 2026-09-14 §4: the page title is the ORG'S NAME in
    // the display face and the quiet line under it is today's date in
    // words (the Today screen's own date line) — see the header meta
    // below. This title is only the fallback when no org is in context
    // (a super admin before choosing one); the legacy "Detailed
    // breakdown…" subtitle is gone.
    title: 'Tachograph',
    description: '',
  },
  managementReport: {
    title: 'Risk report',
    description:
      'Driver findings and DVSA Earned Recognition measures.',
  },
  reportSuite: {
    title: 'Report suite',
    description: 'Compliance and operational reports, saved views and scheduled delivery.',
  },
  exportData: {
    title: 'Export data',
    description: '',
  },
  userManagement: {
    title: 'Users',
    description: 'Who can sign in, and what each person can do.',
  },
  superAdmin: {
    title: 'System admin',
    description: 'Organisations and system-wide settings.',
  },
  orgSettings: {
    title: 'Organisation',
    description: 'Company details, drivers, vehicles, download reminders and the disciplinary process.',
  },
  analogue: {
    title: 'Analogue charts',
    description: 'Photographed tachograph discs: read, checked and filed into the timeline.',
  },
  quarantine: {
    title: "Files we couldn't place",
    description:
      'Uploads we could not match to a driver or truck in DDIR. Add them there, then upload the file again.',
  },
  deepdive: {
    // v3.50.0: standalone Deep-Dive host (?page=deepdive&driver=&date=
    // &vehicle=&mode=). Deliberately NOT in the sidebar nav (Sidebar.jsx
    // hardcodes its sections) — reached via DeepDive's "Open in New Tab"
    // pop-out or a shared URL.
    title: 'Deep Dive',
    description:
      'One day for a driver or a vehicle. The address keeps the view, so it can be shared.',
  },
  fileviewer: {
    // v3.50.1: standalone file inspector (?page=fileviewer&file=…).
    // Same hidden-from-sidebar pattern as deepdive — reached via the
    // Data Centre's </> inspector action or a shared URL.
    title: 'File Viewer',
    description:
      'Everything in one tachograph file: identity, activities, events and faults, speed and specific conditions.',
  },
};

/* ──────────────────────────────────────────────────────────
   App Root
   ────────────────────────────────────────────────────────── */

export default function App() {
  const embedded = isEmbedded();
  useEffect(() => {
    if (!embedded) return undefined;
    const initialTheme = readParentTheme(document.cookie);
    if (initialTheme) applyTheme(initialTheme, document.documentElement);
    const receive = (event) => {
      if (acceptsParentTheme(event, window.parent, window.location.origin, import.meta.env.DEV)) {
        applyTheme(event.data.theme, document.documentElement);
      }
    };
    window.addEventListener('message', receive);
    return () => window.removeEventListener('message', receive);
  }, [embedded]);
  const { isAuthenticated, isSuperAdmin, user, loading } = useAuth();
  const [dashboardNavigation, setDashboardNavigation] = useState({ tab: 'today', records: [] });
  const [dashboardRequest, setDashboardRequest] = useState(null);
  const [activeView, setActiveView] = useState(() =>
    // Default landing page. Super admins are auto-routed to the System
    // Admin view after auth loads — see useEffect below.
    'tachograph'
  );
  // Persisted across iframe reloads. The DDIR embed reloads this app
  // frequently, and losing the collapsed choice each time is a known
  // annoyance — so the expanded/collapsed state is mirrored to
  // localStorage. Storage access is guarded because the embed may run
  // in a restricted context where localStorage throws; we fall back to
  // the in-memory default (expanded) in that case.
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try {
      const stored = window.localStorage.getItem('tp_sidebar_expanded');
      if (stored === null) return true; // default: expanded when unset
      return stored === 'true';
    } catch {
      return true;
    }
  });
  const hasAutoRouted = useRef(false);

  // Auto-route super admins to System Admin view on first load
  useEffect(() => {
    if (!loading && isAuthenticated && isSuperAdmin && !hasAutoRouted.current) {
      hasAutoRouted.current = true;
      setActiveView('superAdmin');
    }
  }, [loading, isAuthenticated, isSuperAdmin]);

  // A12: pick up the ?page=<view> query param that the A5 /embed/<page>
  // endpoint redirects to (e.g. /embed/quarantine → /?page=quarantine).
  // Runs once after auth resolves so the DDIR-embedded iframe lands on
  // the requested view instead of the default Tachograph view.
  const hasPageRouted = useRef(false);
  useEffect(() => {
    if (loading || !isAuthenticated || hasPageRouted.current) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const page = params.get('page');
      if (page && VIEW_META[page]) {
        setActiveView(page);
      }
    } catch {
      // URLSearchParams isn't available in SSR / odd environments;
      // silent fallback to the default view is acceptable.
    }
    hasPageRouted.current = true;
  }, [loading, isAuthenticated]);

  // Show loading spinner while auth state is being determined
  if (loading) {
    return (
      <div className="login-backdrop">
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--accent)' }} />
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const handleNavigate = (key, tab) => {
    if (key === '__toggle_sidebar__') {
      setSidebarExpanded((prev) => {
        const next = !prev;
        try {
          window.localStorage.setItem('tp_sidebar_expanded', String(next));
        } catch {
          // Storage restricted in the embed — keep the in-memory toggle
          // working even if the choice can't be persisted.
        }
        return next;
      });
      return;
    }
    setActiveView(key);
    if (key === 'tachograph' && tab) setDashboardRequest(previous => ({ tab, sequence: (previous?.sequence || 0) + 1 }));
  };

  /* The header (UX pass 2026-10-16, A4). Every page is titled with what it
     is: the tachograph screens with their own name (Today, Calendar, …) in
     both shells, the same words as the navigation. The quiet line under the
     title says how fresh the data is, never today's date (the clock does
     that). Standalone adds the organisation's name; inside DDIR the host's
     own header already carries it. */
  const baseMeta = VIEW_META[activeView] || VIEW_META.tachograph;
  const dataTo = dashboardNavigation.dataTo ? fmtDay(dashboardNavigation.dataTo) : '';
  const meta =
    baseMeta === VIEW_META.tachograph
      ? {
          title: dashboardNavigation.label || 'Today',
          description: [!embedded && orgDisplayName(user), dataTo && `Data to ${dataTo}`].filter(Boolean).join(' · '),
        }
      : baseMeta;

  /* ── Views that require an org context to display data ── */
  const ORG_REQUIRED_VIEWS = new Set([
    'tachograph', 'managementReport', 'reportSuite',
    'exportData', 'orgSettings', 'userManagement', 'deepdive',
    'fileviewer',
    // Charts belong to an org and cost that org a model call each, so a
    // super admin with no org selected is asked to choose one first.
    'analogue',
  ]);

  /* ── Render the active page component ── */
  const renderPage = () => {
    // Super admins must select an org before accessing data views
    if (isSuperAdmin && !user?.org_id && ORG_REQUIRED_VIEWS.has(activeView)) {
      return (
        <div className="state-view">
          <h3 className="state-view-title">Choose an organisation</h3>
          <p className="state-view-body">Choose an organisation from the organisation switcher to see its data.</p>
        </div>
      );
    }

    switch (activeView) {
      case 'tachograph':
        return <TachoDashboard embedded={embedded} navigationRequest={dashboardRequest} onNavigationChange={setDashboardNavigation} />;
      case 'managementReport':
        return <TachoManagementReport />;
      case 'reportSuite':
        return <TachoReportSuite />;
      case 'exportData':
        return <ExportDataPage onOpenReports={() => setActiveView('reportSuite')} />;
      case 'userManagement':
        return <UserManagement />;
      case 'superAdmin':
        return <SuperAdminDashboard />;
      case 'orgSettings':
        return <OrgSettings />;
      case 'analogue':
        // The TP-side chart reader (design 2026-09-18 §6): photographs of
        // paper discs in, read, checked, filed. Reached from the ADMIN
        // section of the sidebar, which only offers it to an org that has
        // the feature.
        return <AnalogueCharts />;
      case 'quarantine':
        // A12: DDIR-embed surface for the operator's quarantine queue.
        // /embed/quarantine (A5) redirects to /?page=quarantine, picked
        // up by the URL-param effect below.
        return <Quarantine />;
      case 'deepdive':
        // v3.50.0: standalone Deep-Dive (?page=deepdive) — opened in a
        // new tab from DeepDive's toolbar so the user keeps their place
        // in the Infringements/Mileage tab they came from.
        return <DeepDivePage />;
      case 'fileviewer':
        // v3.50.1: standalone file inspector (?page=fileviewer&file=…)
        // — opened in a new tab from the Data Centre's </> action.
        return <FileViewerPage />;
      default:
        return <TachoDashboard />;
    }
  };

  return (
    <AndreWorkspace key={`${user?.id}:${user?.org_id}`} page={activeView} pageLabel={baseMeta.title} embedded={embedded} organisation={orgDisplayName(user)}>
    <div className={`admin-shell ${embedded ? 'admin-shell--embedded' : 'admin-shell--standalone'}`}>
      <Sidebar
        isExpanded={sidebarExpanded}
        activeView={activeView}
        onNavigate={handleNavigate}
        embedded={embedded}
        dashboardNavigation={dashboardNavigation}
      />

      <div className="admin-main">
        <PageLayout title={meta.title} description={meta.description} actions={<AskAndreButton />}>
          <Suspense fallback={<PageSpinner />}>{renderPage()}</Suspense>
        </PageLayout>
      </div>
    </div>
    </AndreWorkspace>
  );
}
